// Copyright ©️ 2026 eVolve MEP, LLC

import { type CompanyMember, useEvolvePatch } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { GridMenu } from 'components/Mantine/GridMenu';
import { isNotNil } from 'helpers/isNotNil';
import { strongValues } from 'helpers/strongEntries';
import { useUser } from 'hooks/user/useUser';

import type { HasProductsAvailable } from './types';

export const companyMembersBulkActions = [
  'Assign licenses',
  'Assign roles',
  'Remove roles',
  'Assign admin',
  'Remove admin',
  'Reset password',
  'Resend invite',
  'Delete',
] as const;
export type CompanyMembersBulkAction = (typeof companyMembersBulkActions)[number];

type Props = {
  user: CompanyMember;
  actionSingleUser: (user: CompanyMember, selectedAction: CompanyMembersBulkAction) => void;
  hasProducts: HasProductsAvailable;
};

export const getBulkActions = ({
  userIsAdmin,
  userHasAnyRole,
  companyHasRoles,
  userIsMyUser,
  userIsActive,
}: {
  userIsAdmin?: boolean | undefined;
  userHasAnyRole?: boolean;
  userIsMyUser?: boolean;
  userIsActive?: boolean;
  companyHasRoles: boolean;
}) =>
  [
    {
      value: 'Assign licenses',
    },
    {
      value: 'Assign roles',
      hidden: userIsAdmin,
      disabled: !companyHasRoles,
    },
    {
      value: 'Remove roles',
      hidden: userIsAdmin,
      disabled: !companyHasRoles || !userHasAnyRole,
    },
    {
      value: 'Assign admin',
      hidden: userIsAdmin === true,
    },
    {
      value: 'Remove admin',
      hidden: userIsAdmin === false,
      disabled: userIsMyUser,
    },
    {
      value: 'Reset password',
      disabled: !userIsActive,
    },
    {
      value: 'Resend invite',
    },
    {
      value: 'Delete',
      disabled: userIsMyUser,
    },
  ] satisfies { value: CompanyMembersBulkAction; hidden?: boolean; disabled?: boolean }[];

export const CompanyMemberMenu = ({ user, actionSingleUser, hasProducts }: Props) => {
  const { user: myUser } = useUser();
  const { apiCall: resendInvitation, loading: resending } = useEvolvePatch(
    `admin/user/${user.userId}/resendInvitation`,
  );
  return (
    <GridMenu<CompanyMembersBulkAction>
      options={getBulkActions({
        userHasAnyRole:
          isNotNil(user.mechanicalRoleId) ||
          isNotNil(user.electricalRoleId) ||
          isNotNil(user.shopRoleId) ||
          isNotNil(user.fabricationRoleId),
        companyHasRoles: strongValues(hasProducts).some((hasPool) => hasPool),
        userIsMyUser: user.userId === myUser.userId,
        userIsAdmin: user.isAdmin,
        userIsActive: user.status === 'Active',
      })}
      loading={resending}
      onMenuItemSelected={(action) => {
        if (action === 'Resend invite') {
          void resendInvitation({}).then(() => {
            notifications.show({
              title: 'Successfully sent',
              message: `Invitation resent to ${user.email}`,
              color: 'green',
            });
          });
        } else {
          actionSingleUser(user, action);
        }
      }}
    />
  );
};
